import { useCallback, memo } from 'react';
import {
  ListItem, ListItemAvatar, ListItemText, Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Profile } from '@client/components/Main/Profiles/Profile';
import { Modal } from '@client/components/globals/Modal';
import { getNormalizedProfile } from '@sda/utils/meta';
import { ProfileTypes } from '@sda/enums';
import { useProfileLink } from '@client/components/Main/Profiles/useProfileLink.hook';
import ActPlaceholderImage from '@client/images/placeholders/artist-small.jpg';
import VenuePlaceholderImage from '@client/images/placeholders/venue-small.jpg';
import { SiteContainer } from '../SiteContainer';
import { Avatar } from '../Avatar';
import { ShowDate } from './ShowDate';
import type { OptionalUnion } from '@sda/helpers/helpers.types';
import type { PublicAct } from '@sda/interfaces/act';
import type { PublicVenue } from '@sda/interfaces/venue';

const useStyles = makeStyles()(() => ({
  listItemProfile: {
    display: 'inline-flex',
    paddingBottom: 0,
    paddingTop: 0,
    width: 'auto',

    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

type ReviewProfileProps = OptionalUnion<PublicAct, PublicVenue> & {
  readonly selectedProfileType: ProfileTypes;
  readonly selectedProfileId: string;
  readonly disableProfileClick?: boolean;
  readonly showId?: string;
};

export const ReviewProfile = memo<ReviewProfileProps>(({
  selectedProfileId, selectedProfileType, disableProfileClick, showId,
  ...profile
}) => {
  const { classes } = useStyles();

  const handleProfileButtonClick = useCallback(() => {
    Modal.show({
      title: 'View Profile',
      fullScreen: true,
      maxWidth: 'narrow',
      buttons: [],
      scroll: 'body',
      body: (
        <SiteContainer
          maxWidth="narrow"
          disableGutters
        >
          <Profile
            {...getNormalizedProfile(selectedProfileType, { ...profile, id: selectedProfileId })}
          />
        </SiteContainer>
      ),
    });
  }, [selectedProfileId, selectedProfileType, profile]);

  const profileLinkProps = useProfileLink(selectedProfileType, profile, handleProfileButtonClick);
  const params = disableProfileClick ? {} : {
    ...profileLinkProps,
    button: true,
  };

  return (
    <ListItem
      id="sda-view-profile-review-button"
      aria-label="view profile"
      className={classes.listItemProfile}
      disableGutters
      {...params}
    >
      <ListItemAvatar>
        <Avatar
          size="small"
          images={profile.publicImageUrl}
          fallback={selectedProfileType === ProfileTypes.ACT ? ActPlaceholderImage : VenuePlaceholderImage}
          alt={profile.name}
        />
      </ListItemAvatar>

      <ListItemText
        disableTypography
        primary={(
          <Typography
            variant="body2"
            color="textSecondary"
            className="break-word"
          >
            {profile.name}

            {Boolean(showId) && <ShowDate showId={showId} />}
          </Typography>
        )}
      />
    </ListItem>
  );
});
